
import { computed, defineComponent, onBeforeMount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Collapsible from '@/components/Collapsible.vue';
import ChartSettingsModal from '@/components/ChartSettingsModal.vue';
import ConcentrationChart from '@/components/ConcentrationChart.vue';
import DatePicker from '@/components/DatePicker.vue';
import Spinner from '@/components/Spinner.vue';
import { useChartsStore } from '@/store/charts';
import { useRequestDataStore } from '@/store/request-data';
import { useReportStore } from '@/store/report';
import { PredictionTraits } from '@/service/models/request/computation/computing-traits/prediction-traits';
import { DatesRange } from '@/utilities/types/dates-range';
import { Duration } from '@/utilities/types/duration';
import { ComputingOption } from '@/service/models/request/computation/computing-option';
import { ComputationResult } from '@/service/models/response/computation-result';
import { DataPrediction } from '@/service/models/response/computation/data-responses/data-prediction';
import PkParameters from './PkParameters.vue';

export default defineComponent({
    components: {
        ChartSettingsModal,
        Collapsible,
        ConcentrationChart,
        DatePicker,
        PkParameters,
        Spinner,
    },

    setup()
    {
        const chartStore = useChartsStore();
        const requestDataStore = useRequestDataStore();
        const reportStore = useReportStore();
        const router = useRouter();

        const isSettingsModalOpen = ref(false);

        const validateButtonDisabled = computed(() => {
            return (
                reportStore.pkParameters.parameters.size === 0 ||
                reportStore.steadyStateStats === null
            );
        });

        // Default value for next control date
        if (reportStore.nextControlDate === null) {
            reportStore.nextControlDate = new Date();
            reportStore.nextControlDate.setMonth(reportStore.nextControlDate.getMonth() + 1);
        }

        function handleValidate()
        {
            reportStore.validationDate = new Date();

            router.push("/app/reports");
        }

        onBeforeMount(() => {
            // The PkParameters component is going to request again for PK parameters, so we can clear the old ones
            // from the report.
            reportStore.pkParameters.parameters.clear();
        });

        // Load statistics object for the selected adjustment
        onMounted(async () => {
            // There is currently no way to tell when the steady state will be reached using the API, so
            // let's take the latest date we can, which is the last cycle before the end of the treatment
            const lastDosage = requestDataStore.lastDosageInSelectedAdjustment;
            if (!lastDosage) {
                throw new Error("No dosage found in the selected adjustment");
            }

            const steadyStateMoment = new DatesRange(
                lastDosage.getPreviousDoseDateFrom(Duration.fromSeconds(1).subFrom(lastDosage.end)),
                lastDosage.end
            );

            // Prepare computation request
            const computationRequest = requestDataStore.makeComputationRequest(new PredictionTraits(
                steadyStateMoment,
                20,
                new ComputingOption(
                    requestDataStore.computationParameterTypeForAdjustmentRequest,
                    ComputingOption.CompartmentOption.ALL_ACTIVE_MOIETIES,
                    true,
                    false,
                    false
                )
            ));

            // Run API query
            const query = requestDataStore.makeComputationQuery([computationRequest], true);
            const result = await requestDataStore.makeApiRequest(query).load() as ComputationResult;
            const response = result.getResponseById(computationRequest.requestId);

            if (!(response.data instanceof DataPrediction)) {
                throw Error("Adjustment statistics response received from API is not a <dataPrediction>");
            }

            // Store the statistics response in the store
            reportStore.steadyStateStats = response.data.cycleData[0].statistics;
        });

        return {
            chartStore,
            requestDataStore,
            validateButtonDisabled,
            reportStore,
            isSettingsModalOpen,
            handleValidate,
        };
    },
});
